import React, { RefObject } from "react";
import "./InputSearch.scss";
import classNames from "classnames";
import { debounce } from "@react3l/react3l/helpers";

interface InputSearchProps {
  value?: string;
  isMaterial?: boolean;
  title?: string;
  isError?: boolean;
  disabled?: boolean;
  placeHolder?: string;
  className?: string;
  onChange?: (value: string) => void;
}

type DebouncedChangeHandler = ((nextValue: string) => void) & {
  cancel?: () => void;
};

function InputSearch(props: InputSearchProps) {
  const {
    value,
    isMaterial,
    title,
    disabled,
    placeHolder,
    className,
    onChange,
  } = props;

  const [internalValue, setInternalValue] = React.useState<string>("");

  const inputRef: RefObject<HTMLInputElement> = React.useRef<HTMLInputElement>(
    null
  );

  const debouncedOnChange = React.useMemo<DebouncedChangeHandler>(
    () =>
      debounce((nextValue: string) => {
        if (typeof onChange === "function") {
          onChange(nextValue);
        }
      }, 400) as DebouncedChangeHandler,
    [onChange]
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setInternalValue(nextValue);
      debouncedOnChange(nextValue);
    },
    [debouncedOnChange]
  );

  React.useEffect(() => () => debouncedOnChange.cancel?.(), [
    debouncedOnChange,
  ]);

  const handleClearInput = React.useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      setInternalValue("");
      debouncedOnChange.cancel?.();
      if (typeof onChange === "function") {
        onChange(null);
      }
      inputRef.current?.focus();
    },
    [debouncedOnChange, onChange]
  );

  React.useEffect(() => {
    if (value) {
      setInternalValue(value);
    } else {
      setInternalValue("");
    }
  }, [value]);

  return (
    <>
      <div className="input-search__container">
        {title && <div className="input-search__title">{title}</div>}
        <div className="input-search__wrapper">
          <span className="input-search_icon-search">
            <i className="tio-search" />
          </span>
          <input
            type="text"
            onChange={handleChange}
            placeholder={
              placeHolder ? placeHolder : "Nhập nội dung tìm kiếm..."
            }
            ref={inputRef}
            disabled={disabled}
            className={classNames("component__input", {
              "component__input--material": isMaterial,
            })}
          />
          {internalValue ? (
            <i
              className="input-icon input-search__icon tio-clear"
              onClick={handleClearInput}
            ></i>
          ) : (
            className && (
              <i
                className={classNames(
                  "input-icon",
                  "input-search__icon",
                  className
                )}
              ></i>
            )
          )}
        </div>
      </div>
    </>
  );
}

InputSearch.defaultProps = {
  isMaterial: false,
  disabled: false,
  className: "",
};

export default InputSearch;
